/**
 * CancelDialog — cancelar una reserva en la vista previa.
 *
 * Cancelar no borra: la reserva sigue existiendo, se busca y se informa. Se
 * pide motivo y origen porque son justo los datos que después hacen falta para
 * medir cancelaciones. La compensación del reparto por una cancelación sigue
 * sin decidirse y esta pantalla no la decide.
 */
import React, { useEffect, useState } from "react";
import { X } from "@phosphor-icons/react";
import { acquireBodyScrollLock } from "@/lib/bodyScrollLock";
import { formatMoney } from "@/lib/pmsSelectors";
import { CANCELLATION_REASONS, CANCELLED_BY_LABEL } from "@/data/reservationsV2Demo";

export default function CancelDialog({ view, onConfirm, onClose }) {
  const [by, setBy] = useState("guest");
  const [reason, setReason] = useState(CANCELLATION_REASONS[0]);
  const [refund, setRefund] = useState(0);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    const release = acquireBodyScrollLock();
    return () => {
      document.removeEventListener("keydown", onKey);
      release();
    };
  }, [onClose]);

  const paid = view.money.paid_eur;

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" data-testid="cancel-dialog">
      <div className="absolute inset-0 bg-stone-900/40 backdrop-blur-[2px]" onClick={onClose} />
      <div className="absolute inset-x-0 bottom-0 sm:inset-0 sm:m-auto sm:h-fit sm:max-w-md max-h-[90vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl bg-white dark:bg-stone-950 border border-stone-200 dark:border-stone-800 pb-[calc(1rem_+_env(safe-area-inset-bottom,0px))] sm:pb-0">
        <div className="px-4 pt-4 pb-3 border-b border-stone-100 dark:border-stone-800 flex items-start justify-between gap-3">
          <div className="font-serif-dv text-lg text-stone-900 dark:text-stone-100">
            Cancelar la reserva
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            data-testid="cancel-close"
            className="w-11 h-11 -mr-2 -mt-2 flex items-center justify-center text-stone-500 shrink-0"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-4 space-y-3">
          <label className="block">
            <span className="block text-[10px] uppercase tracking-widest text-stone-400 mb-1">
              Quién cancela
            </span>
            <select
              value={by}
              onChange={(e) => setBy(e.target.value)}
              data-testid="cancel-by"
              className="dv-input w-full text-sm min-h-[44px]"
            >
              {Object.entries(CANCELLED_BY_LABEL).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="block text-[10px] uppercase tracking-widest text-stone-400 mb-1">
              Motivo
            </span>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="cancel-reason"
              className="dv-input w-full text-sm min-h-[44px]"
            >
              {CANCELLATION_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </label>

          <label className="block">
            <span className="block text-[10px] uppercase tracking-widest text-stone-400 mb-1">
              Importe a reembolsar
            </span>
            <input
              type="number"
              min="0"
              value={refund}
              onChange={(e) => setRefund(Number(e.target.value))}
              data-testid="cancel-refund"
              className="dv-input w-full text-sm min-h-[44px]"
            />
            <span className="block text-[11px] text-stone-400 mt-1">
              Cobrado hasta ahora: {formatMoney(paid, view.money.currency)}. La política de cancelación se configura en
              Ajustes; aquí solo se anota lo que se devuelve.
            </span>
          </label>

          <p className="text-[11px] text-stone-400">
            La reserva no se borra: queda cancelada, con su motivo, y sigue contando para los
            informes de cancelaciones.
          </p>
        </div>

        <div className="px-4 py-3 border-t border-stone-100 dark:border-stone-800 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="min-h-[44px] px-3 rounded-full text-xs border border-stone-300 dark:border-stone-700 text-stone-600 dark:text-stone-300"
          >
            Volver
          </button>
          <button
            type="button"
            onClick={() => {
              onConfirm({
                cancelled_by: by,
                reason,
                refunded_eur: refund,
                retained_revenue_eur: Math.max(0, paid - refund),
              });
              onClose();
            }}
            data-testid="cancel-confirm"
            className="min-h-[44px] px-4 rounded-full text-xs bg-red-600 text-white hover:bg-red-700"
          >
            Cancelar la reserva
          </button>
        </div>
      </div>
    </div>
  );
}
